import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import MenuIcon from "@mui/icons-material/Menu";
import { Avatar, SwipeableDrawer } from "@mui/material";
import List from "@mui/material/List";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { useAuth } from "../../hooks/auth";

import logoImg from "../../assets/logo.png";
import useWindowDimensions from "../../utils/useWindowDimensions";
import {
  AppMenuInfo,
  AppMenuStyled,
  ListItemMenuSmall,
  ListItemTileMenuSmall,
  LogoLarge,
  LogoSmall,
  MenuButton,
  MenuContentLarge,
  MenuContentSmall,
  MenuItemButton,
  MenuItemCustom,
  MenuStyled,
  UserMenu,
} from "./AppMenu.styled";

const AppMenu = () => {
  const navigate = useNavigate();
  const pathLocation = useLocation();
  const { width } = useWindowDimensions();
  const { user, signOut } = useAuth();

  const [anchorUserMenu, setAnchorUserMenu] = useState<null | HTMLElement>(
    null,
  );
  const open = Boolean(anchorUserMenu);

  const [isScreenSmall, setIsScreenSmall] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [toogleMenuPoint] = useState(1100);

  const userSelected = useMemo(() => {
    return (
      pathLocation.pathname.startsWith("/users") ||
      pathLocation.pathname.startsWith("/user")
    );
  }, [pathLocation.pathname]);

  const categoriesSelected = useMemo(() => {
    return (
      pathLocation.pathname.startsWith("/categories") ||
      pathLocation.pathname.startsWith("/category")
    );
  }, [pathLocation.pathname]);

  const criteriaSelected = useMemo(() => {
    return (
      pathLocation.pathname.startsWith("/criteria-list") ||
      pathLocation.pathname.startsWith("/criteria")
    );
  }, [pathLocation.pathname]);

  const exhibitionsSelected = useMemo(() => {
    return (
      ((pathLocation.pathname.startsWith("/exhibition") ||
        pathLocation.pathname.startsWith("/judgment") ||
        pathLocation.pathname.startsWith("/plant")) &&
        !pathLocation.pathname.startsWith("/judgment-type") &&
        !pathLocation.pathname.startsWith("/judgment-types")) ||
      pathLocation.pathname.startsWith("/home")
    );
  }, [pathLocation.pathname]);

  const judgmentTypesSelected = useMemo(() => {
    return (
      pathLocation.pathname.startsWith("/judgment-type") ||
      pathLocation.pathname.startsWith("/judgment-types")
    );
  }, [pathLocation.pathname]);

  const speciesSelected = useMemo(() => {
    return (
      pathLocation.pathname === "/species" ||
      pathLocation.pathname === "/specie"
    );
  }, [pathLocation.pathname]);

  const collectorsPlantsSelected = useMemo(() => {
    return (
      pathLocation.pathname.startsWith("/collectors/plants") ||
      pathLocation.pathname.startsWith("/home")
    );
  }, [pathLocation.pathname]);

  useEffect(() => {
    if (width < toogleMenuPoint) {
      setIsScreenSmall(true);
      setIsMenuOpen(false);
    } else {
      setIsScreenSmall(false);
    }
  }, [width, toogleMenuPoint]);

  const getAvatar = (): string => {
    try {
      const firstLetter = user?.name?.split("")[0]?.toUpperCase();
      const lastLetter = user?.name
        ?.split(" ")
        [user?.name?.split(" ").length - 1]?.split("")[0]
        ?.toUpperCase();
      return `${firstLetter}${lastLetter}`;
    } catch (error) {
      return user?.name?.split("")[0]?.toUpperCase();
    }
  };

  const getName = () => {
    return user?.name?.split(" ")[0];
  };

  const logout = () => {
    signOut();
    navigate("/");
  };

  const handleNavigate = (to: string) => {
    navigate(to);
    setIsMenuOpen(false);
  };

  const getAppMenuLarge = () => {
    return (
      <MenuContentLarge>
        <LogoLarge onClick={() => navigate("/home")}>
          <img src={logoImg} alt="" />
        </LogoLarge>

        {user?.typeId === 1 && (
          <MenuItemButton
            variant="text"
            onClick={() => handleNavigate("users")}
            selected={userSelected}
          >
            Usuários
          </MenuItemButton>
        )}

        {user?.typeId === 1 && (
          <MenuItemButton
            variant="text"
            onClick={() => handleNavigate("judgment-types")}
            selected={judgmentTypesSelected}
          >
            Tipos de Julgamento
          </MenuItemButton>
        )}

        {user?.typeId === 1 && (
          <MenuItemButton
            variant="text"
            onClick={() => handleNavigate("species")}
            selected={speciesSelected}
          >
            Espécies
          </MenuItemButton>
        )}

        {user?.typeId === 1 && (
          <MenuItemButton
            variant="text"
            onClick={() => handleNavigate("categories")}
            selected={categoriesSelected}
          >
            Categorias
          </MenuItemButton>
        )}

        {user?.typeId === 1 && (
          <MenuItemButton
            variant="text"
            onClick={() => handleNavigate("criteria-list")}
            selected={criteriaSelected}
          >
            Critérios
          </MenuItemButton>
        )}

        {user?.typeId === 1 && (
          <MenuItemButton
            variant="text"
            onClick={() => handleNavigate("exhibitions")}
            selected={exhibitionsSelected}
          >
            Exposições
          </MenuItemButton>
        )}

        {user?.typeId === 2 && (
          <MenuItemButton
            variant="text"
            onClick={() => handleNavigate("exhibitions")}
            selected={exhibitionsSelected}
          >
            Julgamentos
          </MenuItemButton>
        )}

        {user?.typeId === 3 && (
          <MenuItemButton
            variant="text"
            onClick={() => handleNavigate("collectors/plants")}
            selected={collectorsPlantsSelected}
          >
            Minhas Plantas
          </MenuItemButton>
        )}
      </MenuContentLarge>
    );
  };

  const getAppMenuSmall = () => {
    return (
      <MenuContentSmall>
        <LogoSmall
          onClick={() => {
            navigate("/home");
            setIsMenuOpen(false);
          }}
        >
          <img src={logoImg} alt="" />
        </LogoSmall>
        <List>
          {user?.typeId === 1 && (
            <>
              <ListItemMenuSmall
                key="users"
                disablePadding
                onClick={() => {
                  handleNavigate("users");
                }}
              >
                <ListItemTileMenuSmall
                  primary="Usuários"
                  selected={userSelected}
                />
              </ListItemMenuSmall>

              <ListItemMenuSmall
                key="judgment-types"
                disablePadding
                onClick={() => {
                  handleNavigate("judgment-types");
                }}
              >
                <ListItemTileMenuSmall
                  primary="Tipos de Julgamento"
                  selected={judgmentTypesSelected}
                />
              </ListItemMenuSmall>

              <ListItemMenuSmall
                key="species"
                disablePadding
                onClick={() => {
                  handleNavigate("species");
                }}
              >
                <ListItemTileMenuSmall
                  primary="Espécies"
                  selected={speciesSelected}
                />
              </ListItemMenuSmall>

              <ListItemMenuSmall
                key="categories"
                disablePadding
                onClick={() => {
                  handleNavigate("categories");
                }}
              >
                <ListItemTileMenuSmall
                  primary="Categorias"
                  selected={categoriesSelected}
                />
              </ListItemMenuSmall>

              <ListItemMenuSmall
                key="criteria-list"
                disablePadding
                onClick={() => {
                  handleNavigate("criteria-list");
                }}
              >
                <ListItemTileMenuSmall
                  primary="Critérios"
                  selected={criteriaSelected}
                />
              </ListItemMenuSmall>

              <ListItemMenuSmall
                key="exhibitions"
                disablePadding
                onClick={() => {
                  handleNavigate("exhibitions");
                }}
              >
                <ListItemTileMenuSmall
                  primary="Exposições"
                  selected={exhibitionsSelected}
                />
              </ListItemMenuSmall>
            </>
          )}

          {user?.typeId === 2 && (
            <ListItemMenuSmall
              key="exhibitions"
              disablePadding
              onClick={() => {
                handleNavigate("exhibitions");
              }}
            >
              <ListItemTileMenuSmall
                primary="Julgamentos"
                selected={exhibitionsSelected}
              />
            </ListItemMenuSmall>
          )}

          {user?.typeId === 3 && (
            <ListItemMenuSmall
              key="collectors/plants"
              disablePadding
              onClick={() => {
                handleNavigate("collectors/plants");
              }}
            >
              <ListItemTileMenuSmall
                primary="Minhas Plantas"
                selected={collectorsPlantsSelected}
              />
            </ListItemMenuSmall>
          )}
        </List>
      </MenuContentSmall>
    );
  };

  return (
    <AppMenuStyled logged={!!user?.id} isScreenSmall={isScreenSmall}>
      <MenuStyled isScreenSmall={isScreenSmall}>
        {isScreenSmall ? (
          <>
            <MenuButton
              onClick={() => setIsMenuOpen(true)}
              sx={{ position: "absolute", left: -25, top: -17.5 }}
            >
              <MenuIcon sx={{ color: "#5A635A" }} />
            </MenuButton>
            <SwipeableDrawer
              anchor="left"
              open={isMenuOpen}
              onClose={() => setIsMenuOpen(false)}
              onOpen={() => setIsMenuOpen(true)}
              PaperProps={{
                sx: { display: "flex", alignItems: "flex-start" },
              }}
            >
              {getAppMenuSmall()}
            </SwipeableDrawer>
          </>
        ) : (
          getAppMenuLarge()
        )}
      </MenuStyled>

      <AppMenuInfo>
        <MenuButton
          variant="text"
          color="inherit"
          id="menu-button"
          aria-controls={open ? "menu-button" : undefined}
          aria-haspopup="true"
          aria-expanded={open ? "true" : undefined}
          onClick={e => setAnchorUserMenu(e.currentTarget)}
          endIcon={anchorUserMenu ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
        >
          <Avatar sx={{ width: 24, height: 24, fontSize: "12px" }}>
            {getAvatar()}
          </Avatar>
          {width > toogleMenuPoint && (
            <span style={{ marginLeft: "16px" }}>{getName()}</span>
          )}
        </MenuButton>
        <UserMenu
          id="menu-button"
          anchorEl={anchorUserMenu}
          open={open}
          onClose={() => setAnchorUserMenu(null)}
          MenuListProps={{
            "aria-labelledby": "basic-button",
          }}
        >
          {user?.typeId !== 1 && (
            <MenuItemCustom
              sx={{ minWidth: "150px" }}
              onClick={() => {
                setAnchorUserMenu(null);
                navigate("/profile");
              }}
              selected={pathLocation.pathname === "/profile"}
            >
              Meu Perfil
            </MenuItemCustom>
          )}
          <MenuItemCustom
            sx={{ minWidth: "150px" }}
            onClick={() => {
              setAnchorUserMenu(null);
              logout();
            }}
          >
            Sair
          </MenuItemCustom>
        </UserMenu>
      </AppMenuInfo>
    </AppMenuStyled>
  );
};

export default AppMenu;
